<template>
  <div>
    <div class="card modal-wrapper" id="formBuilderCard" :key="updateKey" style="overflow:hidden">
      <div class="card-header">
          <h5 class="card-title text-center">Form Builder</h5>
          <div style="text-align:center;font-size:11px;color:#888;margin-top:-4px;">
            {{ v2Loading ? '⏳ loading config...' : v2Error ? '❌ config error (v1 fallback)' : useV2 ? '⚡ v2 (dynamic)' : '📋 v1 (legacy)' }}
          </div>
          <hr>
      </div>
      <div class="card-body">
				<!-- v2: Dynamic rendering from DB configuration -->
			<div class="row" v-if="useV2" style="max-width: 800px;">
				<template v-if="v2Loading">
					<div class="col-12"><em>Loading form configuration...</em></div>
				</template>
				<template v-else-if="v2Error">
					<div class="col-12">Unable to load form configuration.</div>
				</template>
				<template v-else>
					<div class="col-6">
						<template v-for="entry in v2LeftColumn">
							<div v-if="entry.type === 'group'"
								:key="'g' + entry.Id"
								:class="entry.CssClass"
								:style="entry.BackgroundColor ? 'background-color:' + entry.BackgroundColor : ''">
								<div v-if="entry.ShowLabel" style="text-align:center;font-weight:bold;">{{ entry.GroupName.toUpperCase() }}</div>
								<Checkbox
									v-for="item in entry.items"
									:key="item.Id"
									:label="item.IsRecommended ? item.DisplayName + '<sup>*</sup>' : item.DisplayName"
									:disabled="isInternalOnlySelected && !item.IsInternalOnly"
									v-model="v2ItemStates[item.Id].selected"
									@change="onV2ItemChanged(item)"
								/>
							</div>
							<Checkbox
								v-else
								:key="'i' + entry.Id"
								:label="entry.IsRecommended ? entry.DisplayName + '<sup>*</sup>' : entry.DisplayName"
								:disabled="isInternalOnlySelected && !entry.IsInternalOnly"
								v-model="v2ItemStates[entry.Id].selected"
								@change="onV2ItemChanged(entry)"
							/>
						</template>
					</div>
					<div class="col-6" style="padding-left:30px">
						<template v-for="entry in v2RightColumn">
							<div v-if="entry.type === 'group'"
								:key="'g' + entry.Id"
								:class="entry.CssClass"
								:style="entry.BackgroundColor ? 'background-color:' + entry.BackgroundColor : ''">
								<div v-if="entry.ShowLabel" style="text-align:center;font-weight:bold;">{{ entry.GroupName.toUpperCase() }}</div>
								<Checkbox
									v-for="item in entry.items"
									:key="item.Id"
									:label="item.IsRecommended ? item.DisplayName + '<sup>*</sup>' : item.DisplayName"
									:disabled="isInternalOnlySelected && !item.IsInternalOnly"
									v-model="v2ItemStates[item.Id].selected"
									@change="onV2ItemChanged(item)"
								/>
							</div>
							<Checkbox
								v-else
								:key="'i' + entry.Id"
								:label="entry.IsRecommended ? entry.DisplayName + '<sup>*</sup>' : entry.DisplayName"
								:disabled="isInternalOnlySelected && !entry.IsInternalOnly"
								v-model="v2ItemStates[entry.Id].selected"
								@change="onV2ItemChanged(entry)"
							/>
						</template>
					</div>
					<div v-if="v2Config.SupportsLanguageSelection" class="col-12" style="text-align:center" :key="languageKey">
						Use
						<CheckboxInline label="English" @change="onLanguageSelectionChange(true)" v-model="isEnglishSelected"/>
						<CheckboxInline label="French" @change="onLanguageSelectionChange(false)" v-model="isFrenchSelected"/>
						where choice exists.
					</div>
					<div v-if="v2Config.ShowRecommendationNote" class="col-12 recommendation">
						{{ v2Config.RecommendationNoteText }}
					</div>
				</template>
			</div>

			<div class="row" v-else-if="isHSCA" style="max-width: 800px;">
					<div class="col-6">
						<Checkbox label="Front Cover" @change="onSelectionChanged" v-model="reportIds.isCoverPageEn.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="Front Cover - Digital Solutions" @change="onSelectionChanged" v-model="reportIds.isCoverPageDigitalEn.selected" :disabled="isInternalOnlySelected"/>

						<Checkbox label="Your Team" @change="onSelectionChanged" v-model="reportIds.isYourTeamEn.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="Your Team - Quebec" @change="onSelectionChanged" v-model="reportIds.isYourTeamQuebecEn.selected" :disabled="isInternalOnlySelected"/>

						<div class="exclusive-area">
							<Checkbox label="Proposal" @change="onProposalChanged(10)" v-model="reportIds.propCanada1.selected" :disabled="isInternalOnlySelected"/>
							<Checkbox label="Equipment Contract" @change="onProposalChanged(11)" v-model="reportIds.propCanada2.selected" :disabled="isInternalOnlySelected"/>
						</div>

						<Checkbox label="Terms" @change="onSelectionChanged" v-model="reportIds.isHSCSTerms.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="Financing" @change="onSelectionChanged" v-model="reportIds.isFinancingEn.selected" :disabled="isInternalOnlySelected"/>
						<br>
					</div>
					<div class="col-6">
						<Checkbox label="Dental IT Support - Digital Solutions" @change="onSelectionChanged" v-model="reportIds.isDentalSupoort.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="Backup - Digital Solutions" @change="onSelectionChanged" v-model="reportIds.isBackupDigital.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="Firewall - Digital Solutions" @change="onSelectionChanged" v-model="reportIds.isFirewallDigital.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="SafeCom Imaging - Digital Solutions" @change="onSelectionChanged" v-model="reportIds.isSafeComImagingEn.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="SafeCom Messenger and Encryption - Digital Solutions" @change="onSelectionChanged" v-model="reportIds.isSafeComMessengerEn.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="Back Cover" @change="onSelectionChanged" v-model="reportIds.isBackCoverEn.selected" :disabled="isInternalOnlySelected"/>

						<div class="exclusive-area" style="width: 250px">
							<div style="text-align: center; font-weight: bold;">
									INTERNAL USE ONLY
							</div>

							<Checkbox label="Worksheet" @change="onSelectionChanged" v-model="reportIds.isWorksheet2.selected"/>
						</div>
					</div>
					<div class="col-12" style="text-align: center" key="languageKey">
						Use
						<CheckboxInline label="English" @change="onLanguageSelectionChange(true)" v-model="isEnglishSelected"/>
						<CheckboxInline label="French" @change="onLanguageSelectionChange(false)" v-model="isFrenchSelected"/>
						where choice exists.
					</div>
				</div>

				<div class="row" v-else-if="isHSUS">
					<div class="col-6">
						<Checkbox label="Cover Page<sup>*</sup>" @change="onSelectionChanged" v-model="reportIds.isCoverPage.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="About Us<sup>*</sup>" @change="onSelectionChanged" v-model="reportIds.isAboutUs.selected" :disabled="isInternalOnlySelected"/>

						<div class="exclusive-area">
							<Checkbox label="Proposal" @change="onProposalChanged(1)" v-model="reportIds.proposal1.selected" :disabled="isInternalOnlySelected"/>
							<Checkbox label="Proposal (no totals)" @change="onProposalChanged(2)" v-model="reportIds.proposal2.selected" :disabled="isInternalOnlySelected"/>
						</div>

						<div class="exclusive-area">
							<Checkbox label="Purchase Agreement (w/ Retail and Thrive)<sup>*</sup>" @change="onProposalChanged(3)" v-model="reportIds.proposal3.selected" :disabled="isInternalOnlySelected"/>
							<Checkbox label="Purchase Agreement (w/ Thrive, no Retail)<sup>*</sup>" @change="onProposalChanged(4)" v-model="reportIds.proposal4.selected" :disabled="isInternalOnlySelected"/>
							<Checkbox label="Purchase Agreement (w/ Retail, no Thrive)<sup>*</sup>" @change="onProposalChanged(5)" v-model="reportIds.proposal5.selected" :disabled="isInternalOnlySelected"/>
							<Checkbox label="Purchase Agreement (no Retail, no Thrive)<sup>*</sup>" @change="onProposalChanged(6)" v-model="reportIds.proposal6.selected" :disabled="isInternalOnlySelected"/>
						</div>

						<Checkbox label="Financing Quote<sup>*</sup>" @change="onSelectionChanged" v-model="reportIds.isFinancingQuote.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="Thrive Signature Equipment Tearsheet" @change="onSelectionChanged" v-model="reportIds.isThriveSignatureTearsheet.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="Terms<sup>*</sup>" @change="onSelectionChanged" v-model="reportIds.isTerms.selected" :disabled="isInternalOnlySelected"/>

						<div class="recommendation">
							<sup>*</sup> Items Recommended for a Full Bundle
						</div>

					</div>
					<div class="col-6" style="padding-left: 30px">
						<Checkbox label="Atlas Purchase Agreement" @change="onSelectionChanged" v-model="reportIds.isAtlasPurchaseAgreement.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="EZPay ACH Form" @change="onSelectionChanged" v-model="reportIds.EZPayACHForm.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="ServiceFirst Digital Bundle" @change="onSelectionChanged" v-model="reportIds.isServiceFirstDigital.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="Henry Schein Protect T&C's" @change="onSelectionChanged" v-model="reportIds.HSCAProtectTandCs.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="HS Protect Flyer & Coverage" @change="onSelectionChanged" v-model="reportIds.isHSProtectCoverageSchedule.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="3Shape Renewal" @change="onSelectionChanged" v-model="reportIds.is3ShapeRenewal.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="Carina Comparison Flyer" @change="onSelectionChanged" v-model="reportIds.isCarinaComparisonFlyer.selected" :disabled="isInternalOnlySelected"/>

						<div class="exclusive-area" style="width: 250px">
							<div style="text-align: center; font-weight: bold;">
									INTERNAL USE ONLY
							</div>

							<Checkbox label="Warranty Return Intake" @change="onInternalChanged(1)" v-model="reportIds.isWarrantyReturnIntake.selected"/>
							<Checkbox label="Worksheet" @change="onInternalChanged(2)" v-model="reportIds.isWorksheet.selected"/>
							<Checkbox label="Equipment Installation List" @change="onInternalChanged(3)" v-model="reportIds.isEquipmentInstallationList.selected"/>
						</div>
					</div>
				</div>

				<div class="row" v-else-if="isZAHN">
					<div class="col-6">
						<Checkbox label="Cover<sup>*</sup>" @change="onSelectionChanged" v-model="reportIds.isCoverPageZahn.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="About Us<sup>*</sup>" @change="onSelectionChanged" v-model="reportIds.isAboutUsZahn.selected" :disabled="isInternalOnlySelected"/>

						<div class="exclusive-area">
							<Checkbox label="Proposal" @change="onProposalChanged(12)" v-model="reportIds.proposalZahn1.selected" :disabled="isInternalOnlySelected"/>
							<Checkbox label="Proposal (no totals)" @change="onProposalChanged(13)" v-model="reportIds.proposalZahn2.selected" :disabled="isInternalOnlySelected"/>
							<Checkbox label="Purchase Agreement<sup>*</sup>" @change="onProposalChanged(15)" v-model="reportIds.proposalZahn4.selected" :disabled="isInternalOnlySelected"/>
							</div>

						<Checkbox label="Financing Quote<sup>*</sup>" @change="onSelectionChanged" v-model="reportIds.isFinancingQuoteZahn.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="Terms and Conditions<sup>*</sup>" @change="onSelectionChanged" v-model="reportIds.isTermsZahn.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="Zahn Technical Support Handout" @change="onSelectionChanged" v-model="reportIds.isZahnTechnicalSupport.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="Zahn Academy Handout" @change="onSelectionChanged" v-model="reportIds.isZahnAcademyHandout.selected" :disabled="isInternalOnlySelected"/>

						<Checkbox label="Credit Line Review" @change="onSelectionChanged" v-model="reportIds.isHSDCreditLineReviewZahn.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="EZ Pay ACH Forms" @change="onSelectionChanged" v-model="reportIds.isEZPayACHFormZahn.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="3Shape Renewal" @change="onSelectionChanged" v-model="reportIds.is3ShapeRenewalZahn.selected" :disabled="isInternalOnlySelected"/>

						<Checkbox label="Pre-Training Questionnaire" @change="onSelectionChanged" v-model="reportIds.isZahnPretraining.selected" :disabled="isInternalOnlySelected"/>

						<div class="recommendation">
							<sup>*</sup> Items Recommended for a Full Bundle
						</div>

					</div>
					<div class="col-6" style="padding-left: 30px">
						<Checkbox label="Evaluation Form Auto Finish" @change="onSelectionChanged" v-model="reportIds.isZahnEvaluationAutoFinish.selected" :disabled="isInternalOnlySelected"/>

						<Checkbox label="Xtcera Mill 580 Presite" @change="onSelectionChanged" v-model="reportIds.isZahnPresiteXmill580.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="Xtcera Mill 580 Installation Preparation" @change="onSelectionChanged" v-model="reportIds.isZahnInstallPrepXmill580.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="Xtcera Mill 500 Presite" @change="onSelectionChanged" v-model="reportIds.isZahnPresiteXmill500.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="Xtcera Mill 500 Installation Preparation" @change="onSelectionChanged" v-model="reportIds.isZahnInstallPrepXmill500.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="Xtcera Mill 300 Presite" @change="onSelectionChanged" v-model="reportIds.isZahnPresiteXmill300.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="VHF K5+ Pre-install Site" @change="onSelectionChanged" v-model="reportIds.isZahnVHFK5Preinstall.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="VHF R5 Pre-install Sit" @change="onSelectionChanged" v-model="reportIds.isZahnVHFR5Preinstall.selected" :disabled="isInternalOnlySelected"/>
						<Checkbox label="Inlab MCX5 Pre-installation " @change="onSelectionChanged" v-model="reportIds.isZahnInladMCX5Preinstall.selected" :disabled="isInternalOnlySelected"/>

						<div class="exclusive-area" style="width: 250px">
							<div style="text-align: center; font-weight: bold;">
									INTERNAL USE ONLY
							</div>

							<Checkbox label="Worksheet" @change="onInternalChanged(122)" v-model="reportIds.isZahnWorksheet.selected"/>
							<Checkbox label="Equipment Installation List" @change="onInternalChanged(123)" v-model="reportIds.isZahnEquipmentInstallationList.selected"/>
						</div>
					</div>
				</div>

				<div class="row">
					<div class="col-9">
						<BasicTextInput v-model="fileName" class="mb-2" label="Filename:"/>
					</div>
					<div class="col-3">
						<button @click="generate" :disabled="!itemSelected" class="btn btn-primary generate-button">Generate Forms</button>
					</div>
				</div>
      </div>
    </div>

    <Modal ref="extendedWarrantyDialog">
      <div class="card modal-wrapper">
        <div class="warranty-title-bar">
          <h5 class="card-title text-center mb-0">Extended Warranty</h5>
        </div>
        <div class="p-4">
        <hr>
        <p class="modal-text">Are items on this order eligible for HS Protect Ext. Warranty?</p>
        <p class="modal-text">To add: Configurator > HS Protect Ext Warranty > Category > Associated price band.</p>
        <p class="modal-text">Questions/Concerns: contact Gabriella Catapano</p>
        <div class="btn-wrapper center">
          <button class="btn btn-secondary" @click="cancelGenerate">Cancel</button>
          <button @click="confirmGenerate" class="btn btn-primary">Generate</button>
        </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
  import { mapState } from 'vuex';
  import formBuilderConfigService from '../services/formBuilderConfig.js';

  export default {
    name: "FormBuilder",
    props: {
			reportTypes: {
				type: Array
			},
			orderNumber: {
				type: String
			},
			billingAddressName: {
				type: String
			},
			dealerType: {
				type: String
			},
			installationDate: {
				type: String
			},
			expirationDate: {
				type: String
			},
			isSubmitted: {
				type: Boolean
			}
    },
    data: () => {
        return {
					fileName: '',
					forms: {},
					updateKey: 0,
					isInternalOnlySelected: false,
					isEnglishSelected: true,
					isFrenchSelected: false,
					languageKey: 0,
					generationData: null,
					// v2 dynamic form state
					v2Config: null,
					v2ItemStates: {},
					v2Loading: false,
					v2Error: null,
					reportIds: {
						isCoverPage: {
							id: 70,
							displayName: 'Cover Page',
						},
						isCoverPageEn: {
							id: 79,
							displayName: 'Cover Page (EN)',
							french: 80
						},
						isCoverPageFr: {
							id: 80,
							displayName: 'Cover Page (FR)',
						},
						isCoverPageDigitalEn: {
							id: 81,
							displayName: 'Cover Page - Digital Solutions (EN)',
							french: 82
						},
						isCoverPageDigitalFr: {
							id: 82,
							displayName: 'Cover Page - Digital Solutions (FR)',
						},
						isAboutUs: {
							id: 71,
							displayName: 'About Us',
						},
						isYourTeamEn: {
							id:83,
							displayName: 'Your Team (EN)',
							french: 84,
						},
						isYourTeamFr: {
							id: 84,
							displayName: 'Your Team (FR)',
						},
						isYourTeamQuebecEn: {
							id: 85,
							displayName: 'Your Team - Quebec (EN)',
							french: 86,
						},
						isYourTeamQuebecFr: {
							id: 86,
							displayName: 'Your Team - Quebec (FR)',
						},
						isDentalSupoort: {
							id: 76,
							displayName: 'Dental IT Support - Digital Solutions',
						},
						isBackupDigital: {
							id: 77,
							displayName: 'Backup - Digital Solutions',
						},
						isFirewallDigital: {
							id: 78,
							displayName: 'Firewall - Digital Solutions',
						},
						proposal1: {
							id: 1,
							displayName: 'Proposal',
							isProposal: true,
						},
						proposal2: {
							id: 2,
							displayName: 'Proposal (no totals)',
							isProposal: true,
						},
						proposal3: {
							id: 4,
							displayName: 'Purchase Agreement',
							isProposal: true,
						},
						proposal4: {
							id: 6,
							displayName: 'Purchase Agreement (no retail',
							isProposal: true,
						},
						proposal5: {
							id: 57,
							displayName: 'Purchase Agreement (no Thrive)',
							isProposal: true,
						},
						proposal6: {
							id: 58,
							displayName: 'Purchase Agreement (no retail, no Thrive)',
							isProposal: true,
						},
						isFinancingQuote: {
							id: 73,
							displayName: 'Financing Quote',
						},
						isPaymentOptionsForm: {
							id: 55,
							displayName: 'Payment Options Form',
						},
						isThriveSignatureTearsheet: {
							id: 74,
							displayName: 'Thrive Signature Equipment Tearsheet',
						},
						propCanada1: {
							id: 36,
							displayName: 'Proposal',
							isProposal: true,
						},
						propCanada2: {
							id: 40,
							displayName: 'Equipment Contract',
							isProposal: true,
						},
						isTerms: {
							id: 72,
							displayName: 'Terms',
						},
						isHSCSTerms: {
							id: 91,
							displayName: 'Terms',
						},
						isFinancingEn: {
							id: 87,
							displayName: 'Financing (EN)',
							french: 88,
						},
						isFinancingFr: {
							id: 88,
							displayName: 'Financing (FR)',
						},
						isHSDCreditLineReview: {
							id: 14,
							displayName: 'HSD Credit Line Review',
						},
						EZPayACHForm: {
							id: 15,
							displayName: 'EZPay ACH Form',
						},
						isServiceFirstDigital: {
							id: 56,
							displayName: 'ServiceFirst Digital Bundle',
						},
						HSCAProtectTandCs: {
							id: 68,
							displayName: "Henry Schein Protect T&C's",
						},
						is3ShapeRenewal: {
							id: 20,
							displayName: '3Shape Renewal',
						},
						isWarrantyReturnIntake: {
							id: 61,
							displayName: 'Warranty Return Intake',
							isInternal: true,
						},
						isWorksheet: {
							id: 3,
							displayName: 'Worksheet',
							isInternal: true,
						},
						isWorksheet2: {
							id: 54,
							displayName: 'Worksheet',
							isInternal: true,
						},
						isEquipmentInstallationList: {
							id: 24,
							displayName: 'Equipment Installation List',
							isInternal: true,
						},
						EZPayACHForm: {
							id: 15,
							displayName: 'EZPay ACH Form',
						},
						isBackCoverEn: {
							id: 89,
							displayName: 'Back Cover (EN)',
							french: 90,
						},
						isBackCoverFr: {
							id: 90,
							displayName: 'Back Cover (FR)',
						},
						isSafeComImagingEn: {
							id: 93,
							displayName: 'SafeCom Imaging - Digital Solutions (EN)',
							french: 94,
						},
						isSafeComImagingFr: {
							id: 94,
							displayName: 'SafeCom Imaging - Digital Solutions (FR)',
						},
						isSafeComMessengerEn: {
							id: 95,
							displayName: 'SafeCom Messenger - Digital Solutions (EN)',
							french: 96
						},
						isSafeComMessengerFr: {
							id: 96,
							displayName: 'SafeCom Messenger - Digital Solutions (FR)',
						},
						// Zahn
						isCoverPageZahn: {
							id: 97,
							displayName: 'Cover Page',
						},
						isAboutUsZahn: {
							id: 98,
							displayName: 'About Us',
						},
						proposalZahn1: {
							id: 99,
							displayName: 'Proposal',
							isProposal: true,
						},
						proposalZahn2: {
							id: 100,
							displayName: 'Proposal (no totals)',
							isProposal: true,
						},
						proposalZahn3: {
							id: 101,
							displayName: 'Purchase Agreement',
							isProposal: true,
						},
						proposalZahn4: {
							id: 102,
							displayName: 'Purchase Agreement (with Retail)',
							isProposal: true,
						},
						isFinancingQuoteZahn: {
							id: 103,
							displayName: 'Financing Quote',
						},
						isTermsZahn: {
							id: 104,
							displayName: 'Terms and Conditions',
						},
						isZahnTechnicalSupport: {
							id: 105,
							displayName: 'Zahn Technical Support Handout',
						},
						isZahnAcademyHandout: {
							id: 106,
							displayName: 'Zahn Academy Handout',
						},
						isHSDCreditLineReviewZahn: {
							id: 107,
							displayName: 'Credit Line Review',
						},
						isEZPayACHFormZahn: {
							id: 108,
							displayName: 'EZ Pay ACH Forms',
						},
						is3ShapeRenewalZahn: {
							id: 109,
							displayName: '3Shape Renewal',
						},
						isZahnPretraining: {
							id: 110,
							displayName: 'Pre-Training Questionnaire',
						},
						isZahnEvaluationAutoFinish: {
							id: 111,
							displayName: 'Evaluation Form Auto Finish',
						},
						isZahnPresiteXmill580: {
							id: 112,
							displayName: 'Xtcera Mill 580 Presite',
						},
						isZahnInstallPrepXmill580: {
							id: 113,
							displayName: 'Xtcera Mill 580 Installation Preparation',
						},
						isZahnPresiteXmill500: {
							id: 114,
							displayName: 'Xtcera Mill 500 Presite',
						},
						isZahnInstallPrepXmill500: {
							id: 115,
							displayName: 'Xtcera Mill 500 Installation Preparation',
						},
						isZahnPresiteXmill300: {
							id: 116,
							displayName: 'Xtcera Mill 300 Presite',
						},
						isZahnVHFK5Preinstall: {
							id: 117,
							displayName: 'VHF K5+ Pre-install Site',
						},
						isZahnVHFR5Preinstall: {
							id: 118,
							displayName: 'VHF R5 Pre-install Site',
						},
						isZahnInladMCX5Preinstall: {
							id: 119,
							displayName: 'Inlab MCX5 Pre-installation',
						},
						isZahnWorksheet: {
							id: 122,
							displayName: 'Worksheet',
							isInternal: true,
						},
						isZahnEquipmentInstallationList: {
							id: 123,
							displayName: 'Equipment Installation List',
							isInternal: true,
						},
						// New HSUS forms
						isAtlasPurchaseAgreement: {
							id: 124,
							displayName: 'Atlas Purchase Agreement',
						},
						isHSProtectCoverageSchedule: {
							id: 125,
							displayName: 'HS Protect Flyer & Coverage',
						},
						isCarinaComparisonFlyer: {
							id: 126,
							displayName: 'Carina Comparison Flyer',
						},
					}
				};
    },
    watch: {},
    computed: {
        ...mapState(["currentUser"]),
				itemSelected() {
					return true;
					//return Object.values(this.reportIds).some((report) => report.selected);
				},
				proposalSelected() {
					return Object.values(this.reportIds).some((report) => report.isProposal && report.selected);
				},
				selectedItems() {
					const selected = [];

					for (const prop in this.reportIds) {
						if (this.reportIds[prop].selected && !prop.startsWith('proposal')) {
							selected.push({
								external: !this.reportIds[prop].isInternal,
								prop
							});
						}
					}

					return selected;
				},
				isHSUS() {
					return this.dealerType === 'HSUS';
				},
				isZAHN() {
					return this.dealerType === 'ZAHN';
				},
				isHSCA() {
					return this.dealerType === 'HSCA';
				},
				useV2() {
					return this.v2Config && this.v2Config.UseFormBuilderV2;
				},
				v2ColumnEntries() {
					if (!this.v2Config) return { 0: [], 1: [] };
					const result = { 0: [], 1: [] };
					[0, 1].forEach(col => {
						const groups = this.v2Config.Groups
							.filter(g => g.ColumnIndex === col)
							.map(g => ({
								type: 'group',
								...g,
								items: this.v2Config.Items
									.filter(i => i.FormGroupId === g.Id)
									.sort((a, b) => a.DisplayOrder - b.DisplayOrder),
							}) );
						const standalones = this.v2Config.Items
							.filter(i => i.ColumnIndex === col && !i.FormGroupId)
							.map(i => ({ type: 'item', ...i }));
						result[col] = [...groups, ...standalones].sort((a, b) => a.DisplayOrder - b.DisplayOrder);
					});
					return result;
				},
				v2LeftColumn() { return this.v2ColumnEntries[0]; },
				v2RightColumn() { return this.v2ColumnEntries[1]; },
    },
    mounted() {
			this.fetchV2Config();
			// TODO: for testing
			// this.dealerType = 'HSUS';
			// this.dealerType = 'ZAHN';
			// this.dealerType = 'HSCA';

			/*
			console.log('DEALER TYPE: ', {
				dealerType: this.dealerType,
				isZahn: this.dealerType === 'ZAHN',
			});
			*/

			const rt = [];
			for (const type of this.reportTypes) {
				rt.push({id: type.Id, displayName: type.DisplayName})
			}
		},
    methods: {
			fetchV2Config() {
				this.v2Loading = true;
				formBuilderConfigService.getConfiguration()
					.then(config => {
						const states = {};
						config.Items.forEach(item => { states[item.Id] = { selected: false }; });
						this.v2ItemStates = states;
						this.v2Config = config;
					})
					.catch((err) => { console.error('[FormBuilder] v2 config fetch failed:', err?.response?.data || err?.message || err); this.v2Error = true; })
					.finally(() => { this.v2Loading = false; });
			},
			onV2ItemChanged(item) {
				const nowSelected = this.v2ItemStates[item.Id].selected;
				if (item.IsInternalOnly) {
					if (nowSelected) this.v2ClearNonInternal();
				} else if (nowSelected) {
					this.v2ClearInternal();
					if (item.FormGroupId) {
						const group = this.v2Config.Groups.find(g => g.Id === item.FormGroupId);
						if (group && !group.IsInternalOnly) {
							this.v2Config.Items
								.filter(i => i.FormGroupId === item.FormGroupId && i.Id !== item.Id)
								.forEach(i => { this.v2ItemStates[i.Id].selected = false; });
						}
					}
					// Apply cross-group mutual exclusion (e.g. Proposals vs Purchase Agreements)
					this.v2Config.MutualExclusionGroups
						.filter(meg => !meg.AllowMultiple && meg.FormItemIds.includes(item.Id))
						.forEach(meg => {
							meg.FormItemIds
								.filter(id => id !== item.Id)
								.forEach(id => { if (this.v2ItemStates[id]) this.v2ItemStates[id].selected = false; });
						});
					this.v2ApplyAutoSelect(item.Id);
				}
				this.isInternalOnlySelected = this.v2Config.Items
					.some(i => i.IsInternalOnly && this.v2ItemStates[i.Id] && this.v2ItemStates[i.Id].selected);
				this.v2UpdateFilename();
				this.updateKey++;
			},
			v2ClearNonInternal() {
				this.v2Config.Items.forEach(item => {
					if (!item.IsInternalOnly) this.v2ItemStates[item.Id].selected = false;
				});
			},
			v2ClearInternal() {
				this.v2Config.Items.forEach(item => {
					if (item.IsInternalOnly) this.v2ItemStates[item.Id].selected = false;
				});
			},
			v2ApplyAutoSelect(itemId) {
				this.v2Config.AutoSelectRules
					.filter(r => r.TriggerFormItemId === itemId && r.RuleType === 'SELECT')
					.forEach(r => {
						if (this.v2ItemStates[r.TargetFormItemId]) this.v2ItemStates[r.TargetFormItemId].selected = true;
					});
			},
			v2UpdateFilename() {
				const proposalItem = this.v2Config.Items.find(item => {
					if (!this.v2ItemStates[item.Id] || !this.v2ItemStates[item.Id].selected) return false;
					if (!item.FormGroupId) return false;
					const group = this.v2Config.Groups.find(g => g.Id === item.FormGroupId);
					return group && !group.IsInternalOnly;
				});
				const selected = this.v2Config.Items.filter(i => this.v2ItemStates[i.Id] && this.v2ItemStates[i.Id].selected);
				if (proposalItem) {
					this.fileName = `${this.orderNumber}-${this.billingAddressName}-${proposalItem.DisplayName}.pdf`;
				} else if (selected.length === 1) {
					this.fileName = `${this.orderNumber}-${this.billingAddressName}-${selected[0].DisplayName}.pdf`;
				} else {
					this.fileName = `${this.billingAddressName}-${(new Date()).toISOString().substring(0, 16)}.pdf`;
				}
			},
			v2ShouldShowExtendedWarranty() {
				const group = this.v2Config.Groups.find(g => g.GroupCode === 'PURCHASE_AGREEMENTS');
				if (!group) return false;
				return this.v2Config.Items
					.filter(i => i.FormGroupId === group.Id)
					.some(i => this.v2ItemStates[i.Id] && this.v2ItemStates[i.Id].selected);
			},
			onLanguageSelectionChange(isEnglish) {
				this.isEnglishSelected = isEnglish;
				this.isFrenchSelected = !isEnglish;
				this.languageKey++;
			},
			onSelectionChanged(id) {
				if (Object.values(this.reportIds).some((report) => report.selected && report.isInternal)) {
					this.clearNonInternal();
					this.isInternalOnlySelected = true;
				}
				else {
					this.clearInternalOnly();
					this.isInternalOnlySelected = false;
				}

				if (this.reportIds.propCanada2.selected) {
					this.reportIds.isHSCSTerms.selected = true;
				}

				this.updateFilename();
				this.updateKey++;
			},
			onInternalChanged(id) {
				this.clearNonInternal();

				this.isInternalOnlySelected = true;

				this.isInternalOnlySelected =
					this.reportIds.isWarrantyReturnIntake.selected ||
					this.reportIds.isWorksheet.selected ||
					this.reportIds.isEquipmentInstallationList.selected ||
					this.reportIds.isZahnWorksheet.selected ||
					this.reportIds.isZahnEquipmentInstallationList.selected;

				this.updateFilename();
			},
			onProposalChanged(id) {
				this.clearInternalOnly();

				this.reportIds.proposal1.selected = false;
				this.reportIds.proposal2.selected = false;

				this.reportIds.proposal3.selected = false;
				this.reportIds.proposal4.selected = false;
				this.reportIds.proposal5.selected = false;
				this.reportIds.proposal6.selected = false;

				this.reportIds.propCanada1.selected = false;
				this.reportIds.propCanada2.selected = false;

				this.reportIds.proposalZahn1.selected = false;
				this.reportIds.proposalZahn2.selected = false;
				this.reportIds.proposalZahn3.selected = false;
				this.reportIds.proposalZahn4.selected = false;

				console.log('onProposalChanged', id);

				switch(id) {
					case 1:
						this.reportIds.proposal1.selected = true;
						break;

					case 2:
						this.reportIds.proposal2.selected = true;
						break;

					case 3:
						this.checkDates();
						this.reportIds.proposal3.selected = true;
						this.reportIds.isTerms.selected = true;
						break;

					case 4:
						this.checkDates();
						this.reportIds.proposal4.selected = true;
						this.reportIds.isTerms.selected = true;

						break;

					case 5:
						this.checkDates();
						this.reportIds.proposal5.selected = true;
						this.reportIds.isTerms.selected = true;
						break;

					case 6:
						this.checkDates();
						this.reportIds.proposal6.selected = true;
						this.reportIds.isTerms.selected = true;
						break;

					case 10:
						this.reportIds.propCanada1.selected = true;
						break;

					case 11:
						this.reportIds.propCanada2.selected = true;
						this.reportIds.isHSCSTerms.selected = true;
						break;

					case 12:
						this.checkDates();
						this.reportIds.proposalZahn1.selected = true;
						this.reportIds.isTermsZahn.selected = true;
						break;

					case 13:
						this.checkDates();
						this.reportIds.proposalZahn2.selected = true;
						this.reportIds.isTermsZahn.selected = true;
						break;

					case 14:
						this.checkDates();
						this.reportIds.proposalZahn3.selected = true;
						this.reportIds.isTermsZahn.selected = true;
						break;

					case 15:
						this.checkDates();
						this.reportIds.proposalZahn4.selected = true;
						this.reportIds.isTermsZahn.selected = true;
						break;

					case 57:
						this.checkDates();
						break;

					case 58:
						this.checkDates();
						break;
				};

				this.updateFilename();
				this.updateKey++;
			},
			checkDates() {
				if (!this.isSubmitted && (!this.installationDate || !this.expirationDate)) {
					this.$store.dispatch('ADD_NOTIFICATION', { text: `Installation Date and Expiration Date must be set`, type: 'warning' });
				}
			},
			updateFilename() {
				const displayName = this.getDisplayName();

				if (this.proposalSelected) {
					this.fileName = `${this.orderNumber}-${this.billingAddressName}-${displayName}.pdf`
				}
				else if (this.selectedItems.length === 1) {
					this.fileName = `${this.orderNumber}-${this.billingAddressName}-${displayName}.pdf`
				}
				else {
					this.fileName = `${this.billingAddressName}-${(new Date()).toISOString().substring(0, 16)}.pdf`;
				}
			},
			getReportId(prop) {
				return this.reportIds[prop].id;
			},
			getDisplayName() {
				if (this.reportIds.proposal1.selected) {
					return this.reportIds['proposal1'].displayName;
				}
				else if (this.reportIds.proposal2.selected) {
					return this.reportIds['proposal2'].displayName;
				}
				else if (this.reportIds.proposal3.selected) {
					return this.reportIds['proposal3'].displayName;
				}
				else if (this.reportIds.proposal4.selected) {
					return this.reportIds['proposal4'].displayName;
				}
				else if (this.reportIds.proposal5.selected) {
					return this.reportIds['proposal5'].displayName;
				}
				else if (this.reportIds.proposal6.selected) {
					return this.reportIds['proposal6'].displayName;
				}
				else if (this.reportIds.propCanada1.selected) {
					return this.reportIds['propCanada1'].displayName;
				}
				else if (this.reportIds.propCanada2.selected) {
					return this.reportIds['propCanada2'].displayName;
				}
				else if (this.reportIds.proposalZahn1.selected) {
					return this.reportIds['proposalZahn1'].displayName;
				}
				else if (this.reportIds.proposalZahn2.selected) {
					return this.reportIds['proposalZahn2'].displayName;
				}
				else if (this.reportIds.proposalZahn3.selected) {
					return this.reportIds['proposalZahn3'].displayName;
				}
				else if (this.reportIds.proposalZahn4.selected) {
					return this.reportIds['proposalZahn4'].displayName;
				}
				else if (this.selectedItems.length === 1) {
					const items = this.selectedItems;
					const prop = this.reportIds[items[0].prop];
					return prop ? prop.displayName : '';
				}

				return `${this.billingAddressName}-${(new Date()).toISOString().substring(0, 16)}`;
			},
			clearNonInternal() {
				for (const reportId in this.reportIds) {
					if (!this.reportIds[reportId].isInternal) {
						this.reportIds[reportId].selected = false;
					}
				}
			},
			clearInternalOnly() {
				for (const reportId in this.reportIds) {
					if (this.reportIds[reportId].isInternal) {
						this.reportIds[reportId].selected = false;
					}
				}
			},
			isProposalSelected() {
				// Check if any proposal form is selected
				return (
					this.reportIds.proposal1.selected ||
					this.reportIds.proposal2.selected ||
					this.reportIds.proposalZahn1.selected ||
					this.reportIds.proposalZahn2.selected ||
					this.reportIds.propCanada1.selected
				);
			},
			isPurchaseAgreementSelected() {
				// Check if any Purchase Agreement form is selected
				return (
					this.reportIds.proposal3.selected ||
					this.reportIds.proposal4.selected ||
					this.reportIds.proposal5.selected ||
					this.reportIds.proposal6.selected ||
					this.reportIds.proposalZahn3.selected ||
					this.reportIds.proposalZahn4.selected
				);
			},
			shouldShowExtendedWarrantyDialog() {
				//return (this.isHSUS || this.isZAHN) && this.isPurchaseAgreementSelected();
				return (this.isHSUS) && this.isPurchaseAgreementSelected();
			},
			cancelGenerate() {
				// Close the dialog without generating
				this.$refs.extendedWarrantyDialog.closeModal();
			},
			confirmGenerate() {
				// Close the dialog and proceed with generation
				this.$refs.extendedWarrantyDialog.closeModal();
				this.emitGenerate();
			},
			emitGenerate() {
				// Emit the generate event with the prepared data
				this.$emit('generate', this.generationData);
			},
			generate() {
				if (this.useV2) {
					const reports = this.v2Config.Items
						.filter(item => this.v2ItemStates[item.Id] && this.v2ItemStates[item.Id].selected)
						.map(item => ({
							ReportId: (this.isFrenchSelected && item.FrenchReportMappingId)
								? item.FrenchReportMappingId
								: item.ReportMappingId,
						}) );
					if (reports.length === 0) { alert('Please select at least one form to generate.'); return; }
					this.generationData = { fileName: this.fileName, reports };
					if (this.v2ShouldShowExtendedWarranty()) {
						this.$refs.extendedWarrantyDialog.openModal();
					} else {
						this.emitGenerate();
					}
				return;
				}
				const reports = Object.values(this.reportIds).filter((report) => report.selected)
					.map((report) => {
						if (this.isFrenchSelected && report.french && !this.isZAHN) {
							return { ReportId: report.french }
						}

						return { ReportId: report.id }
					} );

				if (reports.length === 0) {
					alert('Please select at least one form to generate.');
					return;
				}

				// Prepare the generation data
				this.generationData = {
					fileName: this.fileName,
					reports
				};

				// Check if we should show the extended warranty dialog
				if (this.shouldShowExtendedWarrantyDialog()) {
					// Show the extended warranty dialog
					this.$refs.extendedWarrantyDialog.openModal();
				} else {
					// Proceed with generation directly
					this.emitGenerate();
				}
			}
    },
    components: { }
}
</script>

<style scoped lang="scss">
  .card {
      max-height: 90vh;
			overflow: scroll;
  }

  #formBuilderCard {
      min-width: 825px;
      min-height: 450px;
  }

	.exclusive-area {
		margin: 15px 0;
		margin-left: -6px;
		border: 1px solid black;
		border-radius: 5px;
		padding: 3px 5px;
		background-color: lightgray;
	}

	.card-body {
		width: 100%;
	}

  .col-4 {
      z-index: 99;
  }

	.tab-button {
    margin-top: 25px;
  }

	.modal-wrapper {
			min-width: 800px;
			display: flex;
			justify-content: space-around;
			align-items: center;
			.card-header {
					min-width: 100%;
			}
			.btn-wrapper {
					margin-top: 25px;
					width: 100%;
					display: flex;
					justify-content: flex-end;
					align-items: flex-end;
					.btn{
							min-width: 125px;
					}
					&.center{
							justify-content: space-around;
							align-items: center;
					}
			}
	}

	.recommendation {
		font: inherit;
		margin-top: 10px;
		margin-bottom: 10px;
		color: rbg(112, 112, 112);
		font-weight: 400;
		font-size: 14px;
	}

	.generate-button {
		margin-top: 23px;
	}

	.warranty-title-bar {
		background-color: #FFEB3B; /* Yellow background */
		padding: 15px;
		width: 100%;
		border-top-left-radius: 0.25rem;
		border-top-right-radius: 0.25rem;
		position: relative; /* For positioning the close button */
	}

	:deep(.modal--content .btn-close) {
		position: absolute;
		top: 10px;
		right: 10px;
		margin-top: 0;
		z-index: 10;
		color: #1A2A57; /* Using the primary color from variables */
		border-color: #1A2A57;
		background-color: white;
		font-weight: bold;
	}
</style>
